import { Container, Graphics } from 'pixi.js';
import type { SetDefinition } from '../../data/types';
import { canopyAt } from '../../shared/full/palette';
import { type SunMoment, momentAt } from '../../shared/palette';
import type { SimState } from '../../sim/state';
import {
  type Curve,
  SCALE_X,
  type SpiralColor,
  canopyLayout,
  spiralPoints,
} from './ambyss-canopy-layout';
import { baseScale } from '../camera';
import type { Family, RenderContext } from '../context';
import type { Vec2 } from '../../shared/vec';
import { decorSet, type Frame } from '../frame';
import { parseHexColor } from '../palette';
import { setTint, releaseDrawing } from '../util';

const WHITE = 0xffffff;
const BLACK = 0x000000;
const SPIRAL_COLORS: readonly SpiralColor[] = [
  'mage',
  'turquoise',
  'or',
  'tank',
  'healer',
  'violet',
];
// Layered strokes blur a painted spiral's edge, as the mock-up's soft filter does.
const SOFT_STROKES = [2, 1.6, 1.25, 0.9, 0.6];
const SHOWING: Record<SunMoment, { sail: number; paint: number }> = {
  crepuscule: { sail: 0.2, paint: 0.17 },
  nuit: { sail: 0.16, paint: 0.32 },
  aube: { sail: 0.22, paint: 0.04 },
  jour: { sail: 0.24, paint: 0 },
};

function scalloped(
  shape: Graphics,
  anchors: readonly Vec2[],
  spans: readonly Omit<Curve, 'from'>[],
) {
  const [first] = anchors;
  if (first === undefined) {
    return shape;
  }
  shape.moveTo(first.x, first.y);
  for (const { control, to } of spans) {
    shape.quadraticCurveTo(control.x, control.y, to.x, to.y);
  }
  return shape.closePath();
}

export function createAmbyss(ctx: RenderContext): Family {
  const root = new Container();
  ctx.layers.decor.addChild(root);
  const ropes = new Graphics();
  const pegs = new Graphics();
  const sail = new Graphics();
  const paint = new Container();
  // Each spiral is baked with its own clip into a texture, tinted as a whole: one sprite instead of stacked strokes.
  const spirals = SPIRAL_COLORS.map(() => {
    const layer = new Container();
    const clip = new Graphics();
    const shape = new Graphics();
    shape.mask = clip;
    layer.addChild(clip, shape);
    return { layer, clip, shape };
  });
  const seams = new Graphics();
  const edge = new Graphics();
  const masts = new Graphics();
  const lights = new Graphics();
  paint.addChild(...spirals.map(({ layer }) => layer));
  root.addChild(ropes, pegs, sail, paint, seams, edge, masts, lights);

  let drawnId = '';
  let styledAt = Number.NaN;
  let bakedKey = '';

  function draw(set: SetDefinition): void {
    const center = { x: set.arena.width / 2, y: set.arena.height / 2 };
    const canopy = canopyLayout(center);

    ropes.clear();
    pegs.clear();
    const peg = 4 * SCALE_X;
    for (const rope of canopy.ropes) {
      ropes.moveTo(rope.from.x, rope.from.y).lineTo(rope.to.x, rope.to.y);
      const along = { x: Math.cos(rope.angle) * peg, y: Math.sin(rope.angle) * peg };
      pegs.poly([
        rope.to.x + along.x - along.y,
        rope.to.y + along.y + along.x,
        rope.to.x - along.x - along.y,
        rope.to.y - along.y + along.x,
        rope.to.x - along.x + along.y,
        rope.to.y - along.y - along.x,
        rope.to.x + along.x + along.y,
        rope.to.y + along.y - along.x,
      ]);
    }
    ropes.stroke({ width: 1.4 * SCALE_X, color: WHITE, alpha: 0.85 });
    pegs.fill(WHITE).stroke({ width: 1, color: BLACK, alpha: 0.35 });

    sail.clear();
    scalloped(sail, canopy.anchors, canopy.scallops).fill(WHITE);
    for (const [index, { clip, shape }] of spirals.entries()) {
      clip.clear();
      scalloped(clip, canopy.anchors, canopy.scallops).fill(WHITE);
      shape.clear();
      for (const spiral of canopy.spirals.filter(({ color }) => color === SPIRAL_COLORS[index])) {
        // Segments shorter than the brush at the very heart of a spiral would spike its joins.
        const points = spiralPoints(spiral)
          .filter(
            ({ x, y }) => Math.hypot(x - spiral.center.x, y - spiral.center.y) >= spiral.width / 2,
          )
          .flatMap(({ x, y }) => [x, y]);
        for (const soft of SOFT_STROKES) {
          shape.poly(points, false);
          shape.stroke({
            width: spiral.width * soft,
            color: WHITE,
            alpha: 0.16,
            cap: 'round',
            join: 'round',
          });
        }
      }
    }

    seams.clear();
    for (const seam of canopy.seams) {
      seams.moveTo(seam.from.x, seam.from.y);
      seams.quadraticCurveTo(seam.control.x, seam.control.y, seam.to.x, seam.to.y);
    }
    seams.stroke({ width: SCALE_X, color: WHITE, alpha: 0.55 });
    const [west, east] = canopy.masts;
    seams.moveTo(west.x, west.y).lineTo(east.x, east.y);
    seams.stroke({ width: 1.6 * SCALE_X, color: WHITE, alpha: 0.55 });

    edge.clear();
    scalloped(edge, canopy.anchors, canopy.scallops).stroke({
      width: 2.2 * SCALE_X,
      color: WHITE,
      alpha: 0.9,
    });

    masts.clear();
    lights.clear();
    for (const mast of canopy.masts) {
      masts.circle(mast.x, mast.y, 8 * SCALE_X).fill(WHITE);
      masts.circle(mast.x, mast.y, 8 * SCALE_X).stroke({ width: 1.2, color: BLACK, alpha: 0.4 });
      lights.circle(mast.x, mast.y, 3 * SCALE_X).fill(WHITE);
    }
  }

  function style(frame: Frame): void {
    const { palette, light } = frame;
    styledAt = frame.fraction;
    const canopy = canopyAt(frame.fraction);
    const toile = parseHexColor(canopy.toile);
    const corde = parseHexColor(canopy.corde);
    const showing = momentAt(SHOWING, frame.fraction);
    const mode = light.additive ? 'add' : 'normal';

    setTint(sail, toile);
    sail.alpha = showing.sail;
    paint.alpha = showing.paint;
    paint.visible = paint.alpha > 0;
    for (const [index, { layer }] of spirals.entries()) {
      const color = SPIRAL_COLORS[index] ?? 'violet';
      setTint(layer, color === 'violet' ? parseHexColor(canopy.violet) : palette[color]);
    }
    for (const shape of [ropes, pegs, seams, edge, masts]) {
      setTint(shape, corde);
    }
    setTint(lights, palette.noyau);
    lights.blendMode = mode;
  }

  return {
    update(_state: SimState, _alpha: number, frame: Frame): void {
      const set = decorSet(frame, 'ambyss');
      if (set !== undefined && set.id !== drawnId) {
        drawnId = set.id;
        bakedKey = '';
        draw(set);
      } else if (set === undefined && drawnId !== '') {
        drawnId = '';
        bakedKey = '';
        releaseDrawing(root);
      }
      root.visible = frame.set.decor === 'ambyss';
      if (!root.visible) {
        return;
      }
      if (frame.fraction !== styledAt) {
        style(frame);
      }
      // Baked as dense as the screen shows them at the usual zoom; drawn again only when that or the blend changes.
      const mode = frame.light.additive ? 'add' : 'normal';
      const resolution = baseScale(frame.camera.screenHeight) * frame.resolution;
      const key = `${mode} ${String(resolution)}`;
      if (key !== bakedKey) {
        bakedKey = key;
        for (const { layer, shape } of spirals) {
          shape.blendMode = mode;
          layer.blendMode = mode;
          layer.cacheAsTexture({ resolution });
          layer.updateCacheTexture();
        }
      }
    },
    destroy(): void {
      root.destroy({ children: true });
    },
  };
}
